import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { markBoardSeen, type BoardInfo } from '../chatv1/transport'

/**
 * WHAT IS NEW ON A BOARD (owner, 2026-10-30: 當白板有更新的時候呢, project chat 裡面那個白板的 Icon 上面可以跳出來一個
 * 警示的小紅點, 提醒使用者可以去看這個 Whiteboard). The service keeps, per person, the highest `rev` of each board they
 * have had in front of them (chat lambda/boards.ts markSeen) and answers it in the room's list as `seen`; every write
 * says who wrote last. A board is news to a person when it has been written past what they saw — unless the last
 * write was their own hand's (their agent's writes are news: it is what they are waiting for).
 *
 * The list lives in the query cache under ['boards', conv] (BoardMenu useBoards), kept current by the room's frames:
 * `board` (someone wrote: the rev and who) and `board_seen` (this person saw it, on any of their devices).
 */

/** Is this board news to this person — the red dot? */
export function isNews(b: BoardInfo, me: string): boolean {
  if (b.last_by === me && !b.last_via) return false
  // Never marked seen: a board written since this was built is news from its first write; one nobody has written to
  // since (no `last_ts`) is no news to anyone.
  const seen = b.seen ?? (b.last_ts === undefined ? b.rev : 0)
  return b.rev > seen
}

/** The board the dot is about: of those that are news, the one written last (the Whiteboard button opens it). */
export function newestNews(boards: BoardInfo[], me: string): BoardInfo | null {
  let best: BoardInfo | null = null
  for (const b of boards) if (isNews(b, me) && (!best || (b.last_ts ?? 0) > (best.last_ts ?? 0))) best = b
  return best
}

/** A write to one of the room's boards (its `board` frame), into the list: the rev, and who wrote it. */
export function withWrite(cur: BoardInfo[], f: { board: string; rev: number; by: string; via?: string }): BoardInfo[] {
  const at = cur.findIndex((b) => b.board === f.board)
  const was = cur[at]
  if (!was || f.rev <= was.rev) return cur
  const next: BoardInfo = { ...was, rev: f.rev, last_by: f.by, last_ts: Date.now() }
  if (f.via) next.last_via = f.via
  else delete next.last_via
  return cur.map((b, i) => (i === at ? next : b))
}

/** This person has seen `board` up to `rev`: only ever up. */
export function withSeen(cur: BoardInfo[], board: string, rev: number): BoardInfo[] {
  const at = cur.findIndex((b) => b.board === board)
  const was = cur[at]
  if (!was || (was.seen ?? -1) >= rev) return cur
  return cur.map((b, i) => (i === at ? { ...b, seen: rev } : b))
}

/** A drag writes many times a second; what was seen is told once it settles. */
const SETTLE_MS = 1500

/**
 * While a board is open in front of the person, what they see of it is seen. The list says so at once (no dot for a
 * board they are looking at); the service hears it once the board settles, and at once when they leave it or the
 * page goes to the background. A page in the background sees nothing.
 */
export function useMarkSeen(convId: string, board: string, rev: number, open: boolean): void {
  const qc = useQueryClient()
  const [visible, setVisible] = useState(() => document.visibilityState === 'visible')
  useEffect(() => {
    const on = () => setVisible(document.visibilityState === 'visible')
    document.addEventListener('visibilitychange', on)
    return () => document.removeEventListener('visibilitychange', on)
  }, [])
  const want = useRef<{ conv: string; board: string; rev: number } | null>(null)
  const sent = useRef(new Map<string, number>())
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const flush = useRef(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    const w = want.current
    want.current = null
    if (!w) return
    const key = `${w.conv}/${w.board}`
    if ((sent.current.get(key) ?? -1) >= w.rev) return
    sent.current.set(key, w.rev)
    // Not told: the dot comes back on the next read of the list, and the next look marks it again.
    markBoardSeen(w.conv, w.board, w.rev).catch(() => sent.current.delete(key))
  })

  useEffect(() => {
    if (!open || !visible || rev <= 0) return
    qc.setQueryData<BoardInfo[]>(['boards', convId], (cur) => (cur ? withSeen(cur, board, rev) : cur))
    want.current = { conv: convId, board, rev }
    if (!timer.current) timer.current = setTimeout(flush.current, SETTLE_MS)
  }, [convId, board, rev, open, visible, qc])

  // Leaving the board — another board, Back, the page hidden — tells it now.
  useEffect(() => {
    const f = flush.current
    return f
  }, [convId, board, open, visible])
}
